import Debug from 'debug'
import { z } from 'zod'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useForm } from '@tanstack/react-form'

import { Country } from '@jotta/grpc-connect-openapi/country'
import { ReactAriaSelectCountry } from '@jotta/ui/ReactAriaSelectCountry'
import { ReactAriaTextField } from '@jotta/ui/ReactAriaTextField'

import { bizApi } from '../BizApi'
import { BizWidget } from '../widgets/BizWidget'

import { normalizeFieldErrors } from './DisplayFieldErrors'

const debug = Debug('jotta:biz:BizCompanyInformationForm')

export function BizCompanyInformationForm() {
  const {
    data: { company },
  } = bizApi.company.get.useSuspenseQuery()
  const mutation = bizApi.company.update.useMutation()
  const { Field, handleSubmit, Subscribe, reset } = useForm({
    defaultValues: {
      ...company,
    },
    onSubmit: async ({ value: { name, orgNo, country } }) => {
      debug('submit', { name, orgNo, country })
      await mutation.mutateAsync({ name, orgNo, country })
      reset()
    },
  })
  return (
    <BizWidget title={t`Company information`}>
      <form
        className="form relative grid max-w-sm grid-cols-1"
        onSubmit={e => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <Subscribe selector={state => state.isSubmitting}>
          {isSubmitting =>
            isSubmitting && <div className="loading loading-absolute"></div>
          }
        </Subscribe>
        <Field
          name="name"
          validators={{
            onChange: z.string().min(1),
          }}
        >
          {({ name, state, handleChange, handleBlur }) => (
            <ReactAriaTextField
              id={name}
              label={t`Company name`}
              description={t`Your business name as you want it to be displayed.`}
              name={name}
              value={state.value}
              onChange={value => handleChange(value)}
              onBlur={handleBlur}
              isRequired
              isInvalid={!!state.meta.errors.length}
              errorMessage={normalizeFieldErrors(state.meta.errors)}
            />
          )}
        </Field>
        <Field
          name="orgNo"
          validators={{
            onChange: z.string().min(1),
          }}
        >
          {({ name, state, handleChange, handleBlur }) => (
            <ReactAriaTextField
              id={name}
              label={t`Organization number`}
              description={t`The company's official VAT number`}
              name={name}
              value={state.value}
              onChange={value => handleChange(value)}
              onBlur={handleBlur}
              isRequired
              isInvalid={!!state.meta.errors.length}
              errorMessage={normalizeFieldErrors(state.meta.errors)}
            />
          )}
        </Field>
        <Field
          name="country"
          validators={{
            onChange: z.nativeEnum(Country),
          }}
        >
          {({ name, state, handleChange, handleBlur }) => (
            <ReactAriaSelectCountry
              name={name}
              selectedKey={state.value}
              onSelectionChange={e => handleChange(e as any)}
              onBlur={handleBlur}
              isRequired
              isInvalid={!!state.meta.errors.length}
              errorMessage={normalizeFieldErrors(state.meta.errors)}
              label={t`Country`}
              description={t`The chosen country and its language will be the default for new users. All users can change their preferred language and country later in his/her account settings page.`}
            />
          )}
        </Field>
        <div className="form-control">
          <div className="label">
            <Trans>Data processing agreement</Trans>
          </div>
          <div className="form-control-description">
            <a
              className="link"
              target="_blank"
              rel="noreferrer"
              href="https://jottacloud.com/resources/dpa/DPA-Jotta-AS-v20221220.pdf"
            >
              <Trans>View the signed data processing agreement</Trans>
            </a>
          </div>
        </div>
        <div className="form-buttons">
          <Subscribe
            selector={state => [state.isSubmitting, state.isDirty] as const}
          >
            {([isSubmitting, isDirty]) => (
              <button
                disabled={isSubmitting || !isDirty}
                type="submit"
                className="btn btn-primary"
              >
                <Trans>Update</Trans>
              </button>
            )}
          </Subscribe>
        </div>
      </form>
    </BizWidget>
  )
}
